import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Toast } from "primereact/toast";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { MercantileType } from "../../models/ParamSettings";
import { srpsCalculation } from "../../services/SprsService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import {
  getAllDocuments,
  addNewDocument,
} from "../../services/NewVersionService";
import { getAllSettings } from "../../services/SettingsService";
import "./InitialWarehouseStatePage.scss";

export interface InitialWarehouseStatePageProps {}

export const InitialWarehouseStatePage = (
  p: InitialWarehouseStatePageProps,
) => {
  const {} = p;

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [productCategory, setProductCategory] = useState<ProductCategory>();
  const [newDocumentOfReceive, setNewDocumentOfReceive] =
    useState<DocumentOfReceive>({
      id: id ?? "",
      status: ReceiveStatus.Finished,
      warehouse: {} as Warehouse,
      warehouseman: "Početno stanje",
      type: undefined,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: 0,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      repromaterialProducts: [],
      repromaterialProductsJSON: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "Početno stanje",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: false,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      finishedDocument: true,
    });

  const [processing, setProcessing] = useState(false);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [settingsApi, setSettingsApi] = useState<any[]>([]);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const [wResp, pcResp, pResp, sResp] = await Promise.all([
          getAllWarehouses(),
          getAllProductCategories(),
          getAllProducts(),
          getAllSettings(),
        ]);

        [wResp, pcResp, pResp, sResp].forEach(ensureSuccess);

        setWarehousesApi(wResp?.data?.result ?? []);
        setProductCategoriesApi(pcResp?.data?.result ?? []);
        setProductsApi(pResp?.data?.result ?? []);
        setSettingsApi(sResp?.data?.result ?? []);
      } catch (e: any) {
        console.error(e);
        //@ts-ignore
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            String(t("Greška pri učitavanju šifarnika")),
          life: 3500,
        });

        setWarehousesApi([]);
        setProductCategoriesApi([]);
        setProductsApi([]);
        setSettingsApi([]);
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toast = useRef(null);

  const handleNewDocumentOfReceive = (prop: string, value: any) => {
    setNewDocumentOfReceive((newDocumentOfReceive) => ({
      ...newDocumentOfReceive,
      [prop]: value,
    }));
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    copyOfNewDocumentOfReceive.warehouse = warehousesApi.find(
      (item) => item.id == value,
    ) as Warehouse;
    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value,
    ) as ProductCategory;
    setProductCategory(copyOfNewProductCategory);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    copyOfNewDocumentOfReceive.product = productsApi.find(
      (item) => item.id == value,
    ) as Product;
    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const onSubmit = async () => {
    setProcessing(true);

    try {
      let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
      if (!copyOfNewDocumentOfReceive.id) {
        // @ts-ignore
        delete copyOfNewDocumentOfReceive.id;
      }

      const whId = copyOfNewDocumentOfReceive.warehouse?.id;
      const prId = copyOfNewDocumentOfReceive.product?.id;

      if (!whId || !prId) {
        throw new Error(String(t("Magacin i roba su obavezni")));
      }

      const selectedDate = new Date(copyOfNewDocumentOfReceive.date as Date);

      // 1) Provera konflikta na serveru: ima li već prometa pre datuma ili već postoji početno stanje
      const from = new Date(2022, 0, 1);
      const to = new Date(selectedDate);
      to.setHours(23, 59, 59, 999);

      const inputsResp = await getAllDocuments(
        true,
        true,
        from,
        to,
        false,
        whId,
      );
      ensureSuccess(inputsResp);

      const inputs = inputsResp?.data?.result ?? [];

      const hasConflict = inputs.some((doc: any) => {
        const docWhId = doc?.warehouse?.id ?? doc?.warehouseId;
        const docPrId = doc?.product?.id ?? doc?.productId;

        const sameWarehouse = String(docWhId) === String(whId);
        const sameProduct = String(docPrId) === String(prId);

        const finished =
          doc?.finishedDocument === true || doc?.isDocumentFinished === true;
        const isInitial =
          String(doc?.warehouseman ?? "").trim() === "Početno stanje";

        const docDateTs = doc?.date ? new Date(doc.date).getTime() : 0;
        const selectedTs = selectedDate.getTime();

        const hasFinishedBefore = finished && docDateTs < selectedTs;

        return sameWarehouse && sameProduct && (isInitial || hasFinishedBefore);
      });

      if (hasConflict) {
        //@ts-ignore
        toast.current?.show({
          severity: "error",
          detail: `${t("Ne možete uneti početnu vrednost stanja u magacin")} ${
            copyOfNewDocumentOfReceive.warehouse?.name
          } ${t("za proizvod")} ${copyOfNewDocumentOfReceive.product?.name}. ${t(
            "Već postoji promet za izabranu vrstu robe pre izabranog datuma za unos početnog stanja ili je već uneto početno stanje",
          )}`,
          life: 5000,
        });
        return;
      }

      // 2) Izračunaj netto + srps
      let categoryType: MercantileType = MercantileType.Wheat;
      const catName = copyOfNewDocumentOfReceive.product?.category?.name;

      if (catName === "Uljana repica") categoryType = MercantileType.OiledBeet;
      if (catName === "Ječam") categoryType = MercantileType.Barley;
      if (catName === "Soja") categoryType = MercantileType.Soya;
      if (catName === "Suncokret") categoryType = MercantileType.Sunflower;
      if (catName === "Kukuruz") categoryType = MercantileType.Corn;

      copyOfNewDocumentOfReceive.netto =
        (copyOfNewDocumentOfReceive.brutto ?? 0) -
        (copyOfNewDocumentOfReceive.tara ?? 0);

      copyOfNewDocumentOfReceive.srps = srpsCalculation(
        categoryType,
        copyOfNewDocumentOfReceive.netto ?? 0,
        copyOfNewDocumentOfReceive?.moisture ?? 0,
        copyOfNewDocumentOfReceive.impurity ?? 0,
        copyOfNewDocumentOfReceive.breakage ?? 0,
        copyOfNewDocumentOfReceive.defect ?? 0,
        copyOfNewDocumentOfReceive.hectoliter as number,
        copyOfNewDocumentOfReceive.calculationMethod as boolean,
        settingsApi,
      )[0];

      // 3) Postavi atribute dokumenta (prijemnica merkantile “početno stanje”)
      copyOfNewDocumentOfReceive.status = ReceiveStatus.Finished;
      copyOfNewDocumentOfReceive.finishedDocument = true;
      copyOfNewDocumentOfReceive.finishedDocumentFirstTime = true;
      copyOfNewDocumentOfReceive.mercantile = true;
      copyOfNewDocumentOfReceive.input = true;
      copyOfNewDocumentOfReceive.warehouseman = "Početno stanje";
      copyOfNewDocumentOfReceive.driverName = "Početno stanje";

      // 4) Snimi na server (kao i sve druge prijemnice)
      const resp = await addNewDocument(copyOfNewDocumentOfReceive);
      if (!(resp?.status === 200 || resp?.status === 201)) {
        throw new Error(String(t("Dokument nije snimljen")));
      }

      //@ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Uspešno dodato novo početno stanje"),
        life: 3000,
      });

      navigate(`/dashboard`);
    } catch (e: any) {
      console.error(e);
      //@ts-ignore
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          String(t("Greška pri čuvanju početnog stanja")),
        life: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  let addButtonSaveStyle: React.CSSProperties = {};
  addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
  addButtonSaveStyle.border = "none";
  addButtonSaveStyle.margin = "0 .5rem";

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title-left">
              {t("Početno stanje magacina za merkantilu")}
            </span>
          </div>
        </div>
        <div className="form-body">
          <DropdownInput
            id="warehouse"
            value={newDocumentOfReceive.warehouse.id}
            onValueChange={handleWarehouseChange}
            optionValue="id"
            optionLabel="name"
            options={warehousesApi.filter((item) => item.virtual !== true)}
            filter={false}
            labelText={t("Magacin *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
          />
          <DateInput
            id="date"
            value={newDocumentOfReceive.date as Date}
            onValueChange={handleNewDocumentOfReceive}
            labelText={`${t("Početno stanje na dan")} *`}
            placeholderText={t("Izaberite datum")}
            icon="bi bi-calendar-check"
            maxDate={new Date()}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
          />
          <DropdownInput
            id="productCategory"
            value={productCategory?.id}
            onValueChange={handleProductCategoryChange}
            optionValue="id"
            optionLabel="name"
            options={productCategoriesApi.filter(
              (item) => item.type == ProductType.MercantileGoods,
            )}
            filter={false}
            labelText={t("Vrsta robe *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
          />
          <DropdownInput
            id="productName"
            value={newDocumentOfReceive.product?.id}
            onValueChange={handleProductChange}
            optionValue="id"
            optionLabel="name"
            options={productsApi.filter(
              (item) => item.category?.id === productCategory?.id,
            )}
            filter={false}
            labelText={t("Naziv robe *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
          />
          <TextInputWithIcon
            id="brutto"
            value={(newDocumentOfReceive.brutto as number) ?? ""}
            onValueChange={handleNewDocumentOfReceive}
            labelText={t("Neto količina *")}
            icon="bi bi-minecart-loaded"
            iconAlign="right"
            placeholderText={t("Unesite neto količinu")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{
              required: true,
              pattern: {
                value: /^\d+$/,
                message: "Please enter a number",
              },
            }}
          />
          <div className="empty-field"></div>
          <TextInputWithIcon
            id="moisture"
            value={(newDocumentOfReceive.moisture as number) ?? ""}
            onValueChange={handleNewDocumentOfReceive}
            labelText={t("Vlaga *")}
            icon="bi bi-clipboard"
            iconAlign="right"
            placeholderText={t("Unesite ponderisanu vlagu")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{
              required: true,
              pattern: {
                value: /\d+\.?\d*/,
                message: "Please enter a number",
              },
            }}
          />
          <TextInputWithIcon
            id="impurity"
            value={newDocumentOfReceive.impurity as number}
            onValueChange={handleNewDocumentOfReceive}
            labelText={t("Primese *")}
            icon="bi bi-clipboard"
            iconAlign="right"
            placeholderText={t("Unesite ponderisanu nečistoću")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{
              required: true,
              pattern: {
                value: /\d+\.?\d*/,
                message: "Please enter a number",
              },
            }}
          />
          {newDocumentOfReceive?.product?.category?.name === "Kukuruz" && (
            <TextInputWithIcon
              id="breakage"
              value={(newDocumentOfReceive.breakage as number) ?? ""}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Lom *")}
              icon="bi bi-clipboard"
              iconAlign="right"
              placeholderText={t("Unesite ponderisani lom")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                pattern: {
                  value: /\d+\.?\d*/,
                  message: "Please enter a number",
                },
              }}
            />
          )}
          {newDocumentOfReceive?.product?.category?.name === "Kukuruz" && (
            <TextInputWithIcon
              id="defect"
              value={(newDocumentOfReceive.defect as number) ?? ""}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Defekt *")}
              icon="bi bi-clipboard"
              iconAlign="right"
              placeholderText={t("Unesite ponderisani defekt")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                pattern: {
                  value: /\d+\.?\d*/,
                  message: "Please enter a number",
                },
              }}
            />
          )}
          {(newDocumentOfReceive?.product?.category?.name === "Pšenica" ||
            newDocumentOfReceive?.product?.category?.name === "Ječam") && (
            <TextInputWithIcon
              id="hectoliter"
              value={(newDocumentOfReceive.hectoliter as number) ?? ""}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Hektolitar *")}
              icon="bi bi-clipboard"
              iconAlign="right"
              placeholderText={t("Unesite ponderisani hektolitar")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                pattern: {
                  value: /\d+\.?\d*/,
                  message: "Please enter a number",
                },
              }}
            />
          )}
          {(newDocumentOfReceive?.product?.category?.name === "Pšenica" ||
            newDocumentOfReceive?.product?.category?.name === "Ječam") && (
            <div className="empty-field"></div>
          )}
          <div className="empty-field"></div>
          <div className="footer-buttons">
            <ButtonInput
              labelText={t("Sačuvaj")}
              onButtonClick={handleSubmit(onSubmit, onError)}
              addStyle={addButtonSaveStyle}
              disabled={processing}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
